import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { useSubjects } from "../SubjectContext";
import { track } from "../lib/analytics";
import { needsBirthMonth } from "../lib/subjectAge";
import { SubjectAvatar } from "../components/SubjectSwitcher";
import HealthProfileExtendedForm from "../components/HealthProfileExtendedForm";
import Sidebar from "./QueryPage/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const COLORS = ["teal", "amber", "rose", "indigo", "emerald", "sky", "violet", "orange"];
const RELATIONSHIPS = [
  "child",
  "parent",
  "grandparent",
  "partner",
  "sibling",
  "other_family",
  "other",
];

export default function SubjectProfilePage() {
  const { subjectId } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { refreshSubjects, setActiveSubjectId } = useSubjects();
  const [detail, setDetail] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [avatarColor, setAvatarColor] = useState("teal");
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [sexAtBirth, setSexAtBirth] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/subjects/${subjectId}`);
      const data = res.data?.data;
      setDetail(data);
      setDisplayName(data.display_name || "");
      setRelationship(data.relationship || "");
      setAvatarColor(data.avatar_color || "teal");
      const tierA = data.health_profile?.tier_a || {};
      setBirthYear(tierA.birth_year ? String(tierA.birth_year) : "");
      setBirthMonth(tierA.birth_month ? String(tierA.birth_month) : "");
      setSexAtBirth(tierA.sex_at_birth || "");
      setActiveSubjectId(subjectId);
    } catch {
      toast.error(t("subjects.loadError"));
      navigate("/people");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const isSelf = detail?.kind === "self";
  const yearNum = birthYear ? parseInt(birthYear, 10) : null;
  const needsMonth =
    !isSelf &&
    yearNum != null &&
    !Number.isNaN(yearNum) &&
    needsBirthMonth(yearNum, birthMonth || null);

  const saveMeta = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/subjects/${subjectId}`, {
        display_name: displayName.trim(),
        avatar_color: avatarColor,
        ...(isSelf ? {} : { relationship }),
      });
      toast.success(t("subjects.saved"));
      await refreshSubjects();
      await load();
    } catch {
      toast.error(t("subjects.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const saveTierA = async () => {
    if (!yearNum || !sexAtBirth) {
      toast.error(t("healthProfile.tierARequired"));
      return;
    }
    if (needsMonth && !birthMonth) {
      toast.error(t("subjects.birthMonthRequired"));
      return;
    }
    setSaving(true);
    try {
      const body = { birth_year: yearNum, sex_at_birth: sexAtBirth };
      if (needsMonth) body.birth_month = parseInt(birthMonth, 10);
      await axios.put(`${API}/subjects/${subjectId}/profile/tier-a`, body);
      toast.success(t("healthProfile.tierASaved"));
      await refreshSubjects();
      await load();
    } catch {
      toast.error(t("healthProfile.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isSelf) {
      navigate("/account");
      return;
    }
    const ok = window.confirm(t("subjects.deleteConfirm"));
    if (!ok) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/subjects/${subjectId}`);
      track("subject_deleted", { subject_kind: "dependent" });
      toast.success(t("subjects.deleted"));
      await refreshSubjects();
      navigate("/people");
    } catch {
      toast.error(t("subjects.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  if (!detail) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 p-4 md:p-8 max-w-3xl space-y-8">
        <div className="flex items-center gap-3">
          <SubjectAvatar subject={detail} />
          <div>
            <h1 className="text-2xl font-semibold">{detail.display_name}</h1>
            <p className="text-sm text-muted-foreground">
              {detail.age_display || "—"}
              {detail.health_profile?.tier_a?.sex_at_birth
                ? ` · ${t(`healthProfile.sex.${detail.health_profile.tier_a.sex_at_birth}`)}`
                : ""}
            </p>
          </div>
        </div>

        <section className="space-y-3">
          <h2 className="text-base font-medium">{t("subjects.settings")}</h2>
          <div className="space-y-1.5">
            <Label>{t("subjects.displayName")}</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          {!isSelf && (
            <div className="space-y-1.5">
              <Label>{t("subjects.relationship")}</Label>
              <Select value={relationship} onValueChange={setRelationship}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`subjects.rel.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("subjects.avatarColor")}</Label>
            <Select value={avatarColor} onValueChange={setAvatarColor}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLORS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`subjects.color.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {detail.consent && (
            <p className="text-xs text-muted-foreground">
              {t("subjects.consentReadonly")
                .replace("{basis}", t(`subjects.consent.${detail.consent.basis}`))
                .replace("{date}", (detail.consent.attested_at || "").slice(0, 10))}
            </p>
          )}
          <Button onClick={saveMeta} disabled={saving}>
            {t("common.save")}
          </Button>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-medium">{t("healthProfile.tierATitle")}</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("healthProfile.birthYear")}</Label>
              <Input
                type="number"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
              />
            </div>
            {needsMonth && (
              <div className="space-y-1.5">
                <Label>{t("subjects.birthMonth")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t("healthProfile.sexAtBirth")}</Label>
            <Select value={sexAtBirth || undefined} onValueChange={setSexAtBirth}>
              <SelectTrigger>
                <SelectValue placeholder={t("healthProfile.selectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {["female", "male", "intersex", "prefer_not_to_say"].map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {t(`healthProfile.sex.${opt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={saveTierA} disabled={saving}>
            {t("healthProfile.saveTierA")}
          </Button>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-medium">{t("healthProfile.extendedTitle")}</h2>
          <HealthProfileExtendedForm
            subjectId={subjectId}
            subjectKind={isSelf ? "self" : "dependent"}
            initialExtended={detail.health_profile?.tier_extended || {}}
            submitLabel={t("healthProfile.saveExtended")}
            onSaved={async () => {
              await refreshSubjects();
              await load();
            }}
          />
        </section>

        <section className="space-y-2 border-t border-border pt-6">
          {isSelf ? (
            <p className="text-sm text-muted-foreground">
              {t("subjects.selfClearHint")}{" "}
              <Link to="/account" className="text-primary hover:underline">
                {t("nav.account")}
              </Link>
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t("subjects.deleteWarning")}</p>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {t("subjects.deletePerson")}
              </Button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
